/**
 * <ingredient-points>
 *
 * Behaviour for the "Puntos de la fórmula" section (sections/ingredient-points.liquid):
 * the "Leer más" dialogs and drag-to-scroll on the card track.
 *
 * The dialogs are native <dialog> elements, so Escape, the focus trap and the stacking all come
 * for free. Stacking is the reason it is worth it here: both the card and the scroller clip their
 * overflow, and the top layer is the only thing that escapes them reliably.
 */

/** Number of dialogs currently holding the page scroll, across every instance on the page. */
let openModalCount = 0;

const SCROLL_LOCK_CLASS = 'ingredient-points-modal-open';
const INLINE_CLASS = 'ingredient-points__modal--inline';

function lockScroll() {
  openModalCount += 1;
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
}

function unlockScroll() {
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) {
    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  }
}

class IngredientPoints extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-points-track]');
    this.openerFor = new WeakMap();
    this.lockedDialogs = new Set();

    this.handleClick = this.handleClick.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
    this.handleDialogKeydown = this.handleDialogKeydown.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);

    this.addEventListener('click', this.handleClick);

    this.dialogs = Array.from(this.querySelectorAll('.ingredient-points__modal'));
    this.dialogs.forEach((dialog) => {
      // Escape is handled twice on purpose. The 'close' event is the documented signal, but it is
      // the only one for a dismissal this element did not start, so a browser that does not
      // deliver it would leave the page scroll-locked for good. The keydown gets there first and
      // `release` is idempotent, so whichever arrives does the work once.
      dialog.addEventListener('close', this.handleDialogClose);
      dialog.addEventListener('keydown', this.handleDialogKeydown);
    });

    this.initDragScroll();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);

    // The editor can tear the section down while a dialog is open; the page must not be left with
    // its scroll locked by a dialog that no longer exists.
    this.lockedDialogs.forEach(() => unlockScroll());
    this.lockedDialogs.clear();

    if (this.dialogs) {
      this.dialogs.forEach((dialog) => {
        dialog.removeEventListener('close', this.handleDialogClose);
        dialog.removeEventListener('keydown', this.handleDialogKeydown);
      });
    }

    this.teardownDragScroll();
  }

  /**
   * Handles every click inside the section: openers, close buttons and backdrop dismissals.
   * @param {MouseEvent} evt - Event object.
   */
  handleClick(evt) {
    if (this.suppressClick) {
      // The pointer was dragged across the track, so this "click" is the end of a scroll gesture.
      evt.preventDefault();
      evt.stopPropagation();
      this.suppressClick = false;
      return;
    }

    const opener = evt.target.closest('[data-points-open]');
    if (opener) {
      this.open(opener.getAttribute('data-points-open'), opener);
      return;
    }

    const closer = evt.target.closest('[data-points-close]');
    if (closer) {
      this.close(closer.closest('.ingredient-points__modal'));
      return;
    }

    // A click on the backdrop of a modal dialog is reported against the dialog itself.
    if (evt.target.matches('.ingredient-points__modal')) {
      this.close(evt.target);
    }
  }

  /**
   * Opens the dialog with the given id.
   * @param {string} id - Dialog element id.
   * @param {Element} opener - Element that triggered the dialog.
   */
  open(id, opener) {
    const dialog = this.dialogs.find((el) => el.id === id);
    if (!dialog || dialog.hasAttribute('open')) return;

    this.openerFor.set(dialog, opener);

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // No <dialog> support: show it as a plain overlay and take over Escape by hand. The focus
      // trap is what is lost, which is the accepted degradation on those browsers.
      dialog.classList.add(INLINE_CLASS);
      dialog.setAttribute('open', '');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      document.addEventListener('keydown', this.handleDocumentKeydown);

      const closeButton = dialog.querySelector('[data-points-close]');
      if (closeButton) closeButton.focus();
    }

    this.lockedDialogs.add(dialog);
    lockScroll();
  }

  /**
   * Closes a dialog and restores the page around it.
   * @param {Element} dialog - Dialog element.
   */
  close(dialog) {
    if (!dialog || !dialog.hasAttribute('open')) return;

    this.release(dialog);

    if (dialog.classList.contains(INLINE_CLASS)) {
      dialog.removeAttribute('open');
      document.removeEventListener('keydown', this.handleDocumentKeydown);
    } else if (typeof dialog.close === 'function') {
      dialog.close();
    }
  }

  /**
   * Gives the page back its scroll and the visitor back their place. Safe to call more than once
   * for the same dialog: the second call finds nothing left to undo.
   * @param {Element} dialog - Dialog element.
   */
  release(dialog) {
    if (!dialog) return;

    if (this.lockedDialogs.has(dialog)) {
      this.lockedDialogs.delete(dialog);
      unlockScroll();
    }

    const opener = this.openerFor.get(dialog);
    this.openerFor.delete(dialog);

    // Deferred to the next task: on Escape this runs while the dialog is still the modal on
    // screen, and focus moved now would be pulled straight back into it. A timeout rather than
    // rAF, because rAF is tied to the frame loop and does not run in a tab that is not painting —
    // returning the visitor's place is not something to make conditional on compositing.
    if (opener) {
      setTimeout(() => {
        if (document.contains(opener)) opener.focus();
      }, 0);
    }
  }

  /**
   * The dialog's own 'close' event — the signal for dismissals this element did not start.
   * @param {Event} evt - Event object.
   */
  handleDialogClose(evt) {
    this.release(evt.target);
  }

  /**
   * @param {KeyboardEvent} evt - Event object.
   */
  handleDialogKeydown(evt) {
    if (evt.key !== 'Escape') return;
    this.release(evt.currentTarget);
  }

  /**
   * Escape for the no-<dialog> fallback, where the key never reaches a dialog to begin with.
   * @param {KeyboardEvent} evt - Event object.
   */
  handleDocumentKeydown(evt) {
    if (evt.key !== 'Escape') return;
    const open = this.dialogs.find((dialog) => dialog.hasAttribute('open'));
    if (open) this.close(open);
  }

  /**
   * Lets the card track be dragged with a mouse. Touch already scrolls natively, and dragging with
   * a finger would fight the browser's own gesture, so this is gated on pointer type.
   */
  initDragScroll() {
    if (!this.track) return;

    this.dragState = null;

    this.handlePointerDown = (evt) => {
      if (evt.pointerType !== 'mouse' || evt.button !== 0) return;
      // A drag that ended outside the track never produced the click it was going to swallow, so
      // clear the flag here rather than leaving it to eat the next real one.
      this.suppressClick = false;
      this.dragState = {
        pointerId: evt.pointerId,
        startX: evt.clientX,
        startScroll: this.track.scrollLeft,
        moved: false,
      };
    };

    this.handlePointerMove = (evt) => {
      if (!this.dragState || evt.pointerId !== this.dragState.pointerId) return;

      const delta = evt.clientX - this.dragState.startX;
      if (!this.dragState.moved) {
        if (Math.abs(delta) < 5) return;
        this.dragState.moved = true;
        this.track.style.scrollSnapType = 'none';
        this.track.style.cursor = 'grabbing';
        this.track.setPointerCapture(this.dragState.pointerId);
      }

      evt.preventDefault();
      this.track.scrollLeft = this.dragState.startScroll - delta;
    };

    this.handlePointerUp = (evt) => {
      if (!this.dragState || evt.pointerId !== this.dragState.pointerId) return;

      if (this.dragState.moved) {
        // Swallow the click this drag is about to produce, so releasing over "Leer más" does not
        // open a dialog the visitor never meant to open.
        this.suppressClick = true;
        this.track.style.scrollSnapType = '';
        this.track.style.cursor = '';
        if (this.track.hasPointerCapture(this.dragState.pointerId)) {
          this.track.releasePointerCapture(this.dragState.pointerId);
        }
      }

      this.dragState = null;
    };

    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
  }

  teardownDragScroll() {
    if (!this.track) return;
    this.track.removeEventListener('pointerdown', this.handlePointerDown);
    this.track.removeEventListener('pointermove', this.handlePointerMove);
    this.track.removeEventListener('pointerup', this.handlePointerUp);
    this.track.removeEventListener('pointercancel', this.handlePointerUp);
  }
}

if (!customElements.get('ingredient-points')) {
  customElements.define('ingredient-points', IngredientPoints);
}
